/* ============================================================================
   popup-pill.js - look and placement of the popup system's reopen pill
   ("Your 10% is waiting"). PL 2026-10-26, placement rewritten 2026-10-31.

   Kept apart from popup-system.js so the pill can be changed without
   re-uploading the main popup files. popup-system.js still decides when the
   pill shows and what it does; this file only styles and places it.

   Look: one light cream capsule like the page's chips and badges, the colour
   swatch strip as the only colour, and the close X inside the capsule.
   Text and X are espresso on cream (well above 4.5:1). Text is 16px and the
   X is a 44px target (WCAG 2.5.8 needs 24px; 44px is the comfortable size).
   Windows high contrast keeps a visible outline.

   Label (PL 2026-11-03, later): short, 16px bold caps with letter spacing, like
   the site's buttons, so the capsule is narrower on phones. The caps come
   from CSS (text-transform), so the label stays sentence case in the setting
   and screen readers read it as words, not letters. Swatch strip 28x16.

   Hover (PL 2026-11-03): the text button used to get a grey tint, but it
   only covers part of the capsule and has square corners, so the grey showed
   as an off-shape patch. Now the whole capsule reacts instead: border and
   shadow deepen and the label underlines. The X keeps its own round tint.
   Not hover-only: the underline also shows on keyboard focus, next to the
   global focus ring.

   Placement (2026-10-31): the pill is pinned with plain CSS (bottom + safe
   area), so the browser moves it with the page on its own and it stays still
   while scrolling. The old version recalculated "top" from JavaScript on every
   scroll and visual-viewport event; on iPhone that ran a frame behind the
   scroll and snapped back, which is the jitter people saw.
   The only JS left: when the theme's sticky Add to Cart bar shows or hides,
   the pill is lifted above it with a transform (GPU, no layout). That runs
   on the bar's own show/hide, never on scroll.

   Also here for now: the Re:amaze chat bubble's inline "transition: all"
   is narrowed to its hover effects, so nothing about its position can
   animate. Moves to assets/merged.css with the drawer CSS batch.
   ========================================================================== */
(function () {
  'use strict';

  var pill = document.querySelector('[data-plps-pill]');
  if (!pill) return;

  /* The #pl-popup-system prefix outranks the section's own pill rules and the
     older merged.css rule that lifted the pill by --pl-bottom-bar. */
  var P = '#pl-popup-system .plps-pill';
  var css = [
    P + '{top:auto !important;bottom:calc(max(16px, env(safe-area-inset-bottom)) + 4px) !important;transform:translate3d(0, calc(-1 * var(--plps-lift, 0px)), 0);transition:transform .25s cubic-bezier(.32,.72,0,1), border-color .2s ease, box-shadow .2s ease !important;gap:0;padding:0 3px 0 0;background:#FFFDF9;color:#1C1208;border:1px solid rgba(28,18,8,.16);border-radius:999px;box-shadow:0 6px 18px rgba(28,18,8,.14);}',
    P + ' .plps-teaser{max-width:calc(100vw - 92px);padding:13px 4px 13px 12px !important;gap:9px !important;background:transparent !important;color:#1C1208 !important;border:0 !important;box-shadow:none !important;font-size:16px !important;font-weight:700;letter-spacing:.1em;text-transform:uppercase;line-height:1.2 !important;text-decoration:none;text-underline-offset:3px;text-decoration-thickness:1px;}',
    P + ' .plps-strip{width:28px;height:16px;}',
    P + ' .plps-pill__x{width:44px;height:44px;flex:none;display:inline-flex;align-items:center;justify-content:center;border-radius:999px;background:transparent;color:#1C1208;box-shadow:none;transition:background-color .2s ease;}',
    P + ' .plps-pill__x svg{width:15px;height:15px;}',
    /* Text: no background patch; the capsule reacts instead. */
    P + ' .plps-teaser:hover,' + P + ' .plps-teaser:focus-visible{background:transparent !important;text-decoration:underline;}',
    P + ':has(.plps-teaser:hover){border-color:rgba(28,18,8,.32);box-shadow:0 8px 22px rgba(28,18,8,.2);}',
    /* X: a 30px round tint centred in its 44px target, so it keeps clear of the
       capsule edge (a full 44px tint nearly filled the capsule's height and
       touched its right edge). Overrides the older dark hover in the section
       and merged.css. */
    P + ' .plps-pill__x:hover{background:radial-gradient(circle, rgba(28,18,8,.08) 14.5px, transparent 15.5px) !important;color:#1C1208;}',
    '@media (forced-colors:active){' + P + '{border:1px solid CanvasText;background:Canvas;}' + P + ' .plps-teaser,' + P + ' .plps-pill__x{color:CanvasText !important;}}',
    '#reamaze-widget{transition:transform .2s ease-in-out, box-shadow .2s ease-in-out, opacity .2s ease-in-out !important;}'
  ].join('\n');
  var style = document.createElement('style');
  style.setAttribute('data-plps-pill-style', '');
  style.textContent = css;
  document.head.appendChild(style);

  /* Lift above the sticky Add to Cart bar only while it is showing. */
  var bar = null;
  function lift() {
    bar = bar && bar.isConnected ? bar : document.querySelector('product-sticky-form');
    var h = 0;
    if (bar && window.getComputedStyle(bar).visibility !== 'hidden') {
      var r = bar.getBoundingClientRect();
      var overlap = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
      if (overlap > 0) h = Math.round(Math.min(overlap, r.height));
    }
    pill.style.setProperty('--plps-lift', h + 'px');
  }
  function watchBar() {
    bar = document.querySelector('product-sticky-form');
    if (!bar || !window.MutationObserver) return;
    var t = 0;
    /* The bar slides in, so measure again once the slide has finished. */
    new MutationObserver(function () { lift(); clearTimeout(t); t = setTimeout(lift, 400); })
      .observe(bar, { attributes: true, attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'] });
  }
  window.addEventListener('resize', lift, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { watchBar(); lift(); });
  else { watchBar(); lift(); }
})();
